import { ref, computed } from 'vue'
import store from '@/store'
import { config } from '@/config.js'
import { useToast } from '@/composables/common/useToast'
import { useDeportistaPlazos } from '@/composables/admin/bases/useDeportistaPlazos'
import { useDeportistaSanciones } from '@/composables/admin/bases/useDeportistaSanciones'
import { useDeportistaLesiones } from '@/composables/admin/bases/useDeportistaLesiones'
import { useDeportistaIndumentarias } from '@/composables/admin/bases/useDeportistaIndumentarias'

export function useDeportistaShow(props) {
    const toast = useToast()

    // ==================== STATE ====================
    const base = ref(null)
    const fields = ref([])
    const loading = ref(false)
    const fetchedTabs = ref({})
    const selectedItem = ref(null)

    // ==================== COMPUTED ====================
    const configuracion = computed(() => store.getters.getConfig)
    const manager = computed(() => store.getters.getFooter)
    const teatro = computed(() => store.getters.getTeatro)

    const url_documentacion = computed(() => {
        if (!base.value || !base.value.dni) return null
        return config.API_BASE +
            '/f/upload/documento?tipo=bases&base_id=' +
            props.base_id +
            '&id=' +
            base.value.dni
    })

    const tabs = computed(() => [
        { key: 'datos-personales', label: 'Datos personales', show: true },
        { key: 'datos-deportivos', label: 'Datos deportivos', show: true },
        { key: 'lesiones', label: 'Lesiones', show: true },
        { key: 'sanciones', label: 'Sanciones', show: true },
        { key: 'transacciones', label: 'Transacciones', show: !!base.value?.transacciones },
        { key: 'indumentarias', label: 'Indumentarias', show: !!base.value?.indumentarias },
        { key: 'plazos', label: 'Plazos', show: !!(base.value?.plazos && base.value.plazos.length > 0) },
    ])

    // ==================== FETCH METHODS ====================

    /**
     * Carga los datos principales del deportista
     */
    async function getBase() {
        const params = {
            base_id: props.base_id,
            lista_id: props.lista_id,
            filters: {
                teatro_id: teatro.value?.id
            }
        }

        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.bases.show'),
                params
            )
            base.value = res.data.data
        } catch (error) {
            console.error('Error fetching deportista:', error)
            toast.error('Error al cargar los datos del deportista')
            throw error
        }
    }

    /**
     * Carga los campos personalizados (custom fields)
     */
    async function getFields() {
        const params = {
            base_id: props.base_id,
            lista_id: props.lista_id,
            filters: {
                teatro_id: teatro.value?.id
            }
        }

        try {
            const res = await store.dispatch(
                config.getVuexAlias('back.bases.fields'),
                params
            )
            fields.value = res.data.data
        } catch (error) {
            console.error('Error fetching fields:', error)
            toast.error('Error al cargar los campos personalizados')
            throw error
        }
    }

    /**
     * Carga base + fields en paralelo, usado en onMounted
     */
    async function fetchAll() {
        loading.value = true

        const results = await Promise.allSettled([
            getBase(),
            getFields()
        ])

        const failed = results.filter(r => r.status === 'rejected')
        if (failed.length === 2) {
            toast.error('Error al cargar los datos del deportista')
        }

        loading.value = false
    }

    /**
     * Refresca base después de mutaciones
     */
    async function refreshBase() {
        await getBase()
    }

    // ==================== SUB-COMPOSABLES ====================

    const {
        pagarPlazo,
        pagarPlazos,
        descontarPlazo,
        generarEnlacePago,
    } = useDeportistaPlazos(props, refreshBase)

    const {
        showAddSancionModal,
        showBorrarSancionModal,
        sancionesFilters,
        updateSancionesSearch,
        updateSancionesFilter,
        getSanciones,
        addSancion,
        borrarSancion,
    } = useDeportistaSanciones(props, base)

    const {
        showAddLesionModal,
        showBorrarLesionModal,
        lesionesSearch,
        lesionesFilter,
        updateLesionesSearch,
        updateLesionesFilter,
        getLesiones,
        addLesion,
        deleteLesion,
    } = useDeportistaLesiones(props, base)

    const {
        showCambiarTallaModal,
        addIndumentaria,
        cambiarTalla,
    } = useDeportistaIndumentarias(props, refreshBase)

    // ==================== RETURN ====================
    return {
        // State
        base,
        fields,
        loading,
        fetchedTabs,
        selectedItem,

        // Modal visibility refs
        showAddSancionModal,
        showBorrarSancionModal,
        showAddLesionModal,
        showBorrarLesionModal,
        showCambiarTallaModal,

        // Computed
        configuracion,
        manager,
        teatro,
        tabs,
        url_documentacion,

        // Lesiones search/filter
        lesionesSearch,
        lesionesFilter,
        updateLesionesSearch,
        updateLesionesFilter,

        // Sanciones search/filter
        sancionesFilters,
        updateSancionesSearch,
        updateSancionesFilter,

        // Fetch methods
        getBase,
        getLesiones,
        getSanciones,
        getFields,
        fetchAll,
        refreshBase,

        // Plazo mutations
        pagarPlazo,
        pagarPlazos,
        descontarPlazo,
        generarEnlacePago,

        // Sanción mutations
        addSancion,
        borrarSancion,

        // Lesión mutations
        addLesion,
        deleteLesion,

        // Indumentaria mutations
        addIndumentaria,
        cambiarTalla,
    }
}
